import React, { useState } from "react"
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import { Add, Delete, PersonAdd } from "@mui/icons-material"
import { api } from "../../../../services/Main/Api"
import { useSnackbar } from "notistack"
import Masks from "../../../../components/Shared/Masks"

const SignatorySection = ({ inspectionId, signatories = [], onRefresh, readOnly }) => {
  const { enqueueSnackbar } = useSnackbar()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    document_number: "",
    phone: "",
  })

  function updateField(field, value) {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  function resetForm() {
    setFormData({ name: "", email: "", document_number: "", phone: "" })
  }

  function handleAddSignatory() {
    if (!formData.name || !formData.email) {
      enqueueSnackbar("Preencha nome e e-mail do signatário.", {
        variant: "warning",
      })
      return
    }

    setSaving(true)
    api
      .post("/property-inspection/signatory/", {
        property_inspection: inspectionId,
        name: formData.name,
        email: formData.email,
        document_number: formData.document_number,
        phone: formData.phone,
      })
      .then(() => {
        enqueueSnackbar("Signatário adicionado com sucesso!", {
          variant: "success",
        })
        resetForm()
        setDialogOpen(false)
        onRefresh()
      })
      .catch((error) => {
        const errData = error?.response?.data
        if (errData?.message) {
          enqueueSnackbar(errData.message, { variant: "error" })
        } else {
          enqueueSnackbar("Erro ao adicionar signatário.", { variant: "error" })
        }
        console.log(error)
      })
      .finally(() => setSaving(false))
  }

  function handleDeleteSignatory(id) {
    if (!window.confirm("Deseja remover este signatário?")) return
    api
      .delete(`/property-inspection/signatory/${id}/`)
      .then(() => {
        enqueueSnackbar("Signatário removido.", { variant: "info" })
        onRefresh()
      })
      .catch((error) => {
        enqueueSnackbar("Erro ao remover signatário.", { variant: "error" })
        console.log(error)
      })
  }

  return (
    <Card sx={{ mt: 3 }}>
      <CardHeader
        title="Signatários"
        action={
          !readOnly && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<PersonAdd />}
              onClick={() => setDialogOpen(true)}
            >
              Adicionar Signatário
            </Button>
          )
        }
      />
      <Divider />
      <CardContent>
        {signatories.length > 0 ? (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>E-mail</TableCell>
                  <TableCell>CPF/CNPJ</TableCell>
                  <TableCell>Telefone</TableCell>
                  {!readOnly && <TableCell align="center">Ações</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {signatories.map((sig) => (
                  <TableRow key={sig.id}>
                    <TableCell>{sig.name}</TableCell>
                    <TableCell>{sig.email}</TableCell>
                    <TableCell>
                      {sig.document_number
                        ? Masks.cpfOrCnpj(sig.document_number)
                        : "—"}
                    </TableCell>
                    <TableCell>{sig.phone || "—"}</TableCell>
                    {!readOnly && (
                      <TableCell align="center">
                        <Tooltip title="Remover">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteSignatory(sig.id)}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
            Nenhum signatário adicionado.
          </Typography>
        )}
      </CardContent>

      {/* Add signatory dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Adicionar Signatário</DialogTitle>
        <Divider />
        <DialogContent>
          <Grid container spacing={2} mt={0}>
            <Grid item xs={12}>
              <TextField
                label="Nome *"
                fullWidth
                size="small"
                value={formData.name}
                onChange={(e) => updateField("name", e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="E-mail *"
                fullWidth
                size="small"
                type="email"
                value={formData.email}
                onChange={(e) => updateField("email", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="CPF/CNPJ"
                fullWidth
                size="small"
                value={Masks.cpfOrCnpj(formData.document_number)}
                onChange={(e) => updateField("document_number", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Telefone"
                fullWidth
                size="small"
                value={formData.phone}
                onChange={(e) => updateField("phone", e.target.value)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2, justifyContent: "space-between" }}>
          <Button onClick={() => setDialogOpen(false)} color="secondary">
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleAddSignatory}
            disabled={saving}
          >
            Adicionar
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  )
}

export default SignatorySection
